import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Trophy } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { redeemActivationCodeFn } from "@/lib/activation.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/activate-account")({
  component: ActivateAccountPage,
  head: () => ({
    meta: [
      { title: "Activate your account — Bracket Goblin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function reasonMessage(
  reason:
    | "not_found"
    | "expired"
    | "already_used"
    | "too_many_attempts"
    | "mismatch",
): string {
  switch (reason) {
    case "expired":
      return "This code has expired. Ask your league admin to send you a new one.";
    case "already_used":
      return "This code has already been used. If you need to log in, try the Forgot password flow.";
    case "too_many_attempts":
      return "Too many incorrect attempts. Ask your league admin to send you a new code.";
    case "not_found":
    case "mismatch":
    default:
      return "That email + code combination didn't match. Double-check the email address and code from your activation email.";
  }
}

function ActivateAccountPage() {
  const navigate = useNavigate();
  const redeem = useServerFn(redeemActivationCodeFn);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedCode = code.replace(/\D/g, "");
    if (!trimmedEmail) return toast.error("Enter your email address.");
    if (trimmedCode.length !== 6) {
      return toast.error("Enter the 6-digit code from your email.");
    }

    setSubmitting(true);
    try {
      const result = await redeem({
        data: { email: trimmedEmail, code: trimmedCode },
      });

      if (!result.ok) {
        toast.error(reasonMessage(result.reason));
        return;
      }

      // Establish session by verifying the server-minted recovery token.
      // This happens entirely client-side after the user tapped the button,
      // so email scanners can never burn it.
      const { error } = await supabase.auth.verifyOtp({
        token_hash: result.tokenHash,
        type: "recovery",
      });
      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success("Code accepted. Set your password to finish.");
      navigate({ to: "/reset-password" });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-neon)]">
            <Trophy className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-foreground">
            Activate your account
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and the 6-digit code we sent you.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-border bg-card p-6"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="border-primary"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="code">6-digit code</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="000000"
              className="border-primary text-center text-2xl tracking-[0.5em] font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Codes expire 24 hours after being sent.
            </p>
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Verifying…" : "Continue"}
          </Button>
        </form>

        <div className="mt-6 space-y-2 text-center text-xs text-muted-foreground">
          <p>
            Already set your password?{" "}
            <Link
              to="/"
              search={{ redirect: undefined }}
              className="font-medium text-foreground hover:underline"
            >
              Sign in
            </Link>
          </p>
          <p>
            Forgot it?{" "}
            <Link
              to="/forgot-password"
              className="font-medium text-foreground hover:underline"
            >
              Reset your password
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
